import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { FilterRendererProps } from '../../DataTable.types';
import { Button, Input } from '../../../';
import styles from './FilterRenderer.module.scss';
import closeIcon from '../../../SideDrawer/close.svg';

export const RangeFilterRenderer: React.FC<FilterRendererProps> = ({ column, value, onChange }) => {
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Initialize local state from the global value
    const getInitialValues = useCallback(() => {
        if (value?.value) {
            return {
                gte: value.value.gte ?? null,
                lte: value.value.lte ?? null,
            };
        }
        return { gte: null, lte: null };
    }, [value]);

    // Local state for gte and lte values
    const [localGte, setLocalGte] = useState<number | null>(() => getInitialValues().gte);
    const [localLte, setLocalLte] = useState<number | null>(() => getInitialValues().lte);

    // Update local state when global state changes
    useEffect(() => {
        const { gte, lte } = getInitialValues();
        setLocalGte(gte);
        setLocalLte(lte);
    }, [getInitialValues]);

    const isApplied = useMemo(() => {
        return localGte !== null || localLte !== null;
    }, [localGte, localLte]);

    /**
     * Update the filter value by adding the gte and lte values to the filter object
     * as numbers in order to easily apply filters on these values.
     * @param newGte - The new gte value
     * @param newLte - The new lte value
     */
    const updateFilter = useCallback(
        (newGte: number | null, newLte: number | null) => {
            // Only apply if at least one value is provided
            if (newGte !== null || newLte !== null) {
                onChange({
                    operator: 'BETWEEN',
                    value: {
                        gte: newGte,
                        lte: newLte,
                    },
                });
            } else {
                onChange(null);
            }
        },
        [onChange],
    );

    // Debounce the update filter to prevent multiple updates
    const debouncedUpdateFilter = useCallback(
        (newGte: number | null, newLte: number | null) => {
            // Clear existing timeout
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

            // Set new timeout for debounced update
            debounceTimeoutRef.current = setTimeout(() => {
                updateFilter(newGte, newLte);
            }, 300);
        },
        [updateFilter],
    );

    const handleClear = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            setLocalGte(null);
            setLocalLte(null);
            // Clear any pending debounced updates
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            onChange(null);
        },
        [onChange],
    );

    const handleGteChange = useCallback(
        (newGteValue: string) => {
            const numericValue = newGteValue === '' ? null : Number(newGteValue);
            if (numericValue !== null && Number.isNaN(numericValue)) return;
            setLocalGte(numericValue);
            debouncedUpdateFilter(numericValue, localLte);
        },
        [localLte, debouncedUpdateFilter],
    );

    const handleLteChange = useCallback(
        (newLteValue: string) => {
            const numericValue = newLteValue === '' ? null : Number(newLteValue);
            if (numericValue !== null && Number.isNaN(numericValue)) return;
            setLocalLte(numericValue);
            debouncedUpdateFilter(localGte, numericValue);
        },
        [localGte, debouncedUpdateFilter],
    );

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className={styles.FilterRenderer}>
            <div className={isApplied ? styles.FilterApplied : styles.FilterContainer}>
                <div className={styles.FilterRow}>
                    <div className={styles.FilterLabel}>{column?.filterConfig?.label || column.headerLabel}</div>
                    <div className={styles.FilterDropdown}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', width: '100%' }}>
                            <Input
                                type={'number'}
                                allowNegative={true}
                                allowDecimal={true}
                                value={localGte ?? ''}
                                placeholder="Minimum"
                                onChange={handleGteChange}
                                unit={column.unit}
                                style={{ marginTop: 0, width: '100%', height: '44px' }}
                                unitContainer={{ minWidth: '10%' }}
                                numberOfDecimals={2}
                                showFloatingLabel={false}
                                unitStyle={{ textOverflow: 'clip' }}
                            />
                            <Input
                                type={'number'}
                                allowNegative={true}
                                allowDecimal={true}
                                value={localLte ?? ''}
                                placeholder="Maximum"
                                onChange={handleLteChange}
                                unit={column.unit}
                                style={{ marginTop: 0, width: '100%', height: '44px' }}
                                unitContainer={{ minWidth: '10%' }}
                                numberOfDecimals={2}
                                showFloatingLabel={false}
                                unitStyle={{ textOverflow: 'clip' }}
                            />
                        </div>
                    </div>
                    {isApplied && (
                        <Button
                            text=""
                            icon={closeIcon}
                            type="reset"
                            onClick={handleClear}
                            variant="white"
                            style={{
                                marginTop: 0,
                                border: '1px solid #E7E7E7',
                                color: '#4D4D4D',
                                fontSize: '0.875rem',
                                padding: '0.5rem 0.75rem',
                                minWidth: 'auto',
                                flexShrink: 0,
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
